import { area, line } from 'd3-shape';
import { axisBottom, axisLeft } from 'd3-axis';
import { bisector, max, min } from 'd3-array';
import { pointer, select } from 'd3-selection';
import { scaleLinear, scaleTime } from 'd3-scale';
import { timeFormat, timeParse } from 'd3-time-format';
import { timeMonth, timeYear } from 'd3-time';
import { formatVolume } from 'utils/helpers';
import { theme } from 'utils/theme';

class TimeSeries {
    constructor(opts) {
        this.container = opts.container;
        this.data = opts.data;
        this.dataPrediction = opts.dataPrediction;
        this.viewWidth = opts.width;
        this.viewHeight = opts.height;
        this.timeParse = timeParse('%Y-%m-%d');
        this.timeFormat = timeFormat('%b %Y');
        this.padding = opts.padding;
        this.xAxisHeight = opts.xAxisHeight;
        this.yAxisWidth = opts.yAxisWidth;
        this.circleRadius = opts.circleRadius;
        this.tooltipWidth = 145;
        if (this.data.length > 0) {
            const dates = this.data.reduce((acc, { trend }) => {
                if (trend.length < 1) return acc;
                return acc.concat(
                    trend.map(({ date }) => this.timeParse(date)),
                );
            }, []);
            this.date0 = new Date(Math.min(...dates));
            this.dateF = new Date(Math.max(...dates));
            this.dateFPrediction = opts.predictionMonths
                ? timeMonth.offset(this.dateF, opts.predictionMonths)
                : this.dateF;
        }
        this.onHover = opts.onHover;
        // TODO: should this be the default configurable via constructor args?
        this.lineColors = [
            theme.neutral_2,
            theme.neutral_3,
            theme.neutral_4,
            theme.neutral_6,
            theme.neutral_5,
        ];
    }

    computePredictions() {
        this.data = this.data.map((data) => ({
            ...data,
            prediction: this.computePrediction(data),
        }));
    }

    computePrediction({ trend, prediction }) {
        if (trend.length < 1) return prediction;

        const lastActual = trend[trend.length - 1];
        const adjustedPrediction = prediction.filter((d) => {
            return this.timeParse(d.date) > this.timeParse(lastActual.date);
        });

        if (adjustedPrediction.length) {
            adjustedPrediction.unshift({
                date: lastActual.date,
                value: lastActual.value,
                upper: lastActual.value,
                lower: lastActual.value,
            });
        }

        return adjustedPrediction;
    }

    getLineColor(idx) {
        // TODO: probably the colors shouldn't cycle through but be guaranteed to match the number of lines
        return this.lineColors[idx % this.lineColors.length];
    }

    init() {
        this.computePredictions();
        const combinedData = this.data.reduce((acc, { trend, prediction }) => {
            return acc.concat([...trend, ...prediction]);
        }, []);
        const lowerValues = combinedData.map(({ lower }) => lower);
        const upperValues = combinedData.map(({ upper }) => upper);
        const yDomainExpansionMultiplier = 1.1;

        while (this.container && this.container.firstChild) {
            this.container.removeChild(this.container.firstChild);
        }

        this.svg = select(this.container)
            .append('svg')
            .attr('width', this.viewWidth)
            .attr('viewBox', `0 0 ${this.viewWidth} ${this.viewHeight}`);

        this.xScale = scaleTime()
            .domain([this.date0, this.dateFPrediction])
            .range([this.yAxisWidth, this.viewWidth - this.padding]);

        this.yScale = scaleLinear()
            .domain([
                min(lowerValues) * yDomainExpansionMultiplier,
                max(upperValues) * yDomainExpansionMultiplier,
            ])
            .range([this.viewHeight - this.xAxisHeight, this.padding]);

        this.area = area()
            .x((d) => this.xScale(this.timeParse(d.date)))
            .y0(this.viewHeight - this.xAxisHeight)
            .y1((d) => this.yScale(d.value));

        this.areaConf = area()
            .x((d) => this.xScale(this.timeParse(d.date)))
            .y0((d) => this.yScale(d.lower))
            .y1((d) => this.yScale(d.upper));

        this.lines = [];
        this.data.forEach((data, idx) => {
            this.lines.push(
                line()
                    .x((d, i) => this.xScale(this.timeParse(d.date)))
                    .y((d) => this.yScale(d.value)),
            );
        });

        const axisYStyle = axisLeft(this.yScale)
            .tickSize(this.viewWidth)
            .ticks(3)
            .tickPadding(0)
            .tickFormat((d) => `${formatVolume(d)}`);

        this.axisY = this.svg
            .append('g')
            .attr('class', 'yAxis')
            .attr('transform', `translate(${this.viewWidth},0)`)
            .call(axisYStyle);

        this.axisY
            .selectAll('line')
            .attr('stroke-dasharray', '1px 5px')
            .attr('stroke', '#c0c0c0');

        const axisXStyle = axisBottom(this.xScale).ticks(timeYear);

        this.axisX = this.svg
            .append('g')
            .attr('class', 'xAxis')
            .attr('transform', `translate(0,${this.viewHeight - this.padding})`)
            .call(axisXStyle);
    }

    mousemove(event) {
        const x = pointer(event, event.target)[0];
        const x0 = this.xScale.invert(x);
        this.focus.select('text').text('');
        this.data.forEach((data, idx) =>
            this.updateNearestDataPoint(x0, data, idx),
        );
    }

    updateNearestDataPoint(x0, { trend, prediction }, idx) {
        const bisectDate = bisector((d) => this.timeParse(d.date)).left;

        const i = bisectDate(trend, x0, 1) - 1;
        const j = bisectDate(prediction, x0, 1) - 1;

        const di = trend[i];
        const dj = prediction[j];

        if (!(di || dj)) return;

        const isPrediction =
            j > 0 &&
            x0 - this.timeParse(di.date) > this.timeParse(dj.date) - x0;
        const d = isPrediction ? dj : di;

        const thisX = this.xScale(this.timeParse(d.date));
        const thisY = this.yScale(d.value);

        const circle = this.focus.select(`circle.marker-${idx}`);
        circle.attr(`transform`, `translate(${thisX},${thisY})`);
        circle.style(
            'fill',
            isPrediction ? 'rgb(132, 69, 255)' : this.getLineColor(idx),
        );

        const date = this.timeFormat(this.timeParse(d.date));
        const volume = formatVolume(d.value);

        const xOffset =
            (prediction.length + trend.length) / 2 < i + j + 2
                ? -(this.tooltipWidth + this.padding)
                : this.padding;
        this.updateTooltip(date, volume, isPrediction, thisX + xOffset);
    }

    updateTooltip(date, volume, isPrediction, xOffset) {
        this.tooltip.attr(`transform`, `translate(${xOffset},${36})`);
        this.tooltip.select('text.date').text(date);
        this.tooltip
            .select('text.volume')
            .text(volume)
            .style('fill', isPrediction ? 'rgb(132, 69, 255)' : '#000000');
        this.tooltip
            .selectAll('text.label')
            .style('fill', isPrediction ? 'rgb(132, 69, 255)' : '#5E5E5E');
    }

    renderData({ trend, prediction }, idx) {
        const line = this.lines[idx];
        this.svg
            .append('path')
            .datum(trend)
            .attr('fill', 'none')
            .attr('stroke', 'none')
            .attr('d', this.area);

        this.svg
            .append('path')
            .datum(trend)
            .attr('fill', 'none')
            .attr('stroke', this.getLineColor(idx))
            .attr('stroke-linejoin', 'round')
            .attr('stroke-linecap', 'round')
            .attr('stroke-width', 3)
            .attr('d', line);

        this.svg
            .append('path')
            .datum(prediction)
            .attr('fill', 'none')
            .attr('stroke', 'none')
            .attr('d', this.area);

        this.svg
            .append('path')
            .datum(prediction)
            .attr('fill', 'rgba(132,69,255,0.05)')
            .attr('stroke', 'none')
            .attr('d', this.areaConf);

        this.svg
            .append('path')
            .datum(prediction)
            .attr('fill', 'none')
            .attr('stroke', 'rgb(132, 69, 255)')
            .attr('stroke-linejoin', 'round')
            .attr('stroke-linecap', 'round')
            .attr('stroke-width', 3)
            .attr('d', line);
    }

    renderTooltip() {
        this.tooltip = this.focus
            .append('g')
            .style('text-transform', 'uppercase')
            .style('transition', 'transform 0.1s');

        this.tooltip
            .append('filter')
            .attr('id', 'drop-shadow')
            .append('feDropShadow')
            .attr('dx', '0')
            .attr('dy', '4')
            .attr('stdDeviation', '4.4')
            .attr('flood-opacity', '0.11');

        this.tooltip
            .append('rect')
            .attr('width', this.tooltipWidth)
            .attr('height', 128)
            .attr('fill', '#ffffff')
            .attr('class', 'tooltip-box')
            .style('filter', 'url(#drop-shadow)');

        this.tooltip
            .append('text')
            .attr('x', this.padding)
            .attr('y', 30)
            .attr('font-size', '12px')
            .attr('fill', '#5E5E5E')
            .attr('class', 'date');

        this.tooltip
            .append('text')
            .attr('x', this.padding)
            .attr('y', 70)
            .attr('font-size', '32px')
            .attr('class', 'volume');

        this.tooltip
            .append('text')
            .text('search')
            .attr('font-size', '10px')
            .attr('x', this.padding)
            .attr('y', 92)
            .attr('class', 'label');

        this.tooltip
            .append('text')
            .text('volume')
            .attr('font-size', '10px')
            .attr('x', this.padding)
            .attr('y', 105)
            .attr('class', 'label');
    }

    render() {
        this.data.forEach((data, idx) => this.renderData(data, idx));

        this.focus = this.svg
            .append('g') // **********
            .style('display', 'none'); // **********

        this.data.forEach((data, idx) => {
            this.focus
                .append('circle')
                .attr('class', `marker-${idx}`)
                .style('fill', this.getLineColor(idx))
                .style('stroke-width', 0)
                .style('stroke', this.getLineColor(idx))
                .attr('r', this.circleRadius);
        });

        this.renderTooltip();

        this.svg
            .append('rect')
            .attr('width', this.viewWidth)
            .attr('height', this.viewHeight)
            .style('fill', 'none')
            .style('pointer-events', 'all')
            .on('mouseover', (event) => {
                this.mousemove(event);
                this.focus.style('display', null);
            })
            .on('mouseout', () => {
                this.focus.style('display', 'none');
            })
            .on('mousemove', (event) => {
                this.mousemove(event);
            });
    }
}

export default TimeSeries;
